/**
 * Frontend half of the story-type vocabulary (the API spells it `category`).
 *
 * The backend owns the closed set in `backend/app/queries/story_type.py`
 * (`STORY_TYPES`), mirrored by the enrichment Lambda's classifier. On the web
 * side the same five strings used to be spelled three times: a filter list in
 * `searchFilters.ts` and two identical color maps inside PromptLabPage —
 * none of them guarded, so a vocabulary change could pass every Python test
 * while the reader's category pills and the prompt-lab badges silently
 * dropped the new type. Route every web spelling through this module;
 * `backend/tests/test_mirror_parity.py` pins it (and the iOS/Android
 * mirrors) to the backend owner.
 */
export const ALL_CATEGORIES = ['launch', 'event', 'practical', 'opinion', 'miscellaneous'] as const;

export type Category = (typeof ALL_CATEGORIES)[number];

/**
 * Filter-pill labels. Keyed `Record<Category, string>` so adding a story
 * type without a label fails the typecheck rather than rendering blank.
 */
export const CATEGORY_LABELS: Record<Category, string> = {
  launch: 'Launches',
  event: 'Events',
  practical: 'Practical',
  opinion: 'Opinion',
  miscellaneous: 'Misc',
};

/** Badge colors for classification results (prompt lab). */
export const CATEGORY_COLORS: Record<Category, string> = {
  launch: '#2563eb',
  event: '#7c3aed',
  practical: '#059669',
  opinion: '#d97706',
  miscellaneous: '#6b7280',
};

/** Label for a category that may come from outside the closed set. */
export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category as Category] ?? category;
}

/** Badge color, or undefined for a value outside the closed set. */
export function categoryColor(category: string): string | undefined {
  return CATEGORY_COLORS[category as Category];
}
